import { ColorField, Hint, Panel } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { contrastRatio } from '@/lib/color';

/**
 * Palette readout plus a live contrast check. The ratio is measured against
 * the design's own overlay color rather than the raw photo, which is what the
 * viewer actually sees.
 */
export function ColorAnalyzer() {
  const analysis = useStore((s) => s.analysis);
  const spec = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);

  if (!analysis) {
    return (
      <Panel title="Color">
        <p className="text-[11px] text-ink-500">Add a photo to extract its palette.</p>
      </Panel>
    );
  }

  const palette = spec?.palette;
  const headline = spec?.blocks.find((b) => b.role === 'headline');
  const ratio =
    palette && headline ? contrastRatio(palette.overlay, headline.color) : null;

  const setPaletteColor = (key: 'accent' | 'primaryText' | 'secondaryText', value: string) => {
    store.updateActive((current) => {
      const next = { ...current, palette: { ...current.palette, [key]: value } };
      // Re-point the blocks that were using the old value so a palette edit
      // actually changes the canvas rather than just the swatch.
      if (key === 'accent') {
        next.blocks = next.blocks.map((b) =>
          b.role === 'kicker'
            ? { ...b, color: value }
            : b.emphasis
              ? { ...b, emphasis: { ...b.emphasis, color: value } }
              : b,
        );
        next.decor = next.decor.map((d) => ({ ...d, color: value }));
      }
      if (key === 'primaryText') {
        next.blocks = next.blocks.map((b) =>
          b.role === 'headline' ? { ...b, color: value } : b,
        );
      }
      if (key === 'secondaryText') {
        next.blocks = next.blocks.map((b) =>
          b.role === 'subtitle' ? { ...b, color: value } : b,
        );
      }
      return next;
    });
  };

  return (
    <Panel title="Color">
      <div>
        <span className="field-label">Extracted from the photo</span>
        <div className="flex overflow-hidden rounded-md">
          {analysis.dominantColors.map((color) => (
            <div
              key={color.hex}
              title={`${color.hex} — ${(color.weight * 100).toFixed(0)}% of the image`}
              className="h-9"
              style={{ background: color.hex, flexGrow: Math.max(0.15, color.weight) }}
            />
          ))}
        </div>
        <p className="mt-1.5 font-mono text-[9px] uppercase text-ink-500">
          {analysis.dominantColors.map((c) => c.hex).join('  ')}
        </p>
      </div>

      {palette && (
        <div className="mt-3 space-y-0.5 border-t border-white/[0.06] pt-2.5">
          <ColorField
            label="Primary text"
            value={palette.primaryText}
            onChange={(v) => setPaletteColor('primaryText', v)}
          />
          <ColorField
            label="Secondary text"
            value={palette.secondaryText}
            onChange={(v) => setPaletteColor('secondaryText', v)}
          />
          <ColorField
            label="Accent"
            value={palette.accent}
            onChange={(v) => setPaletteColor('accent', v)}
          />
        </div>
      )}

      {ratio !== null && (
        <div className="mt-3 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-[0.14em] text-ink-400">
              Headline contrast
            </span>
            <span
              className={`font-mono text-xs tabular-nums ${
                ratio >= 4.5
                  ? 'text-emerald-400'
                  : ratio >= 3
                    ? 'text-amber-400'
                    : 'text-red-400'
              }`}
            >
              {ratio.toFixed(1)}:1
            </span>
          </div>
          <p className="mt-1 text-[10px] leading-relaxed text-ink-500">
            {ratio >= 4.5
              ? 'Passes WCAG AA at any size.'
              : ratio >= 3
                ? 'Passes AA for large text only — fine for a headline, tight for the subtitle.'
                : 'Below the large-text threshold. Raise the overlay strength or switch the text color.'}
          </p>
          <Hint>
            Measured against the overlay color the design settled on, not the raw
            photo — the photo behind a scrim contributes less than it appears to.
          </Hint>
        </div>
      )}
    </Panel>
  );
}
